import { useAuth } from '@clerk/clerk-react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { ApiError } from '@/lib/api/client'
import { getMe } from '@/lib/api/me'
import type { Me } from '@/lib/types/me'

type AuthAction = 'claim' | 'post' | 'report' | 'manage'

const ACTION_COPY: Record<AuthAction, string> = {
  claim: 'claim a link',
  post: 'post a link',
  report: 'report a link',
  manage: 'manage your links',
}

interface MeContextValue {
  me: Me | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  /** Signed-in, email verified, and account active — may call gated APIs. */
  canMutate: boolean
  messageFor: (action: AuthAction) => string | null
}

const MeContext = createContext<MeContextValue | null>(null)

async function waitForToken(
  getToken: () => Promise<string | null>,
  attempts = 8,
): Promise<string | null> {
  for (let i = 0; i < attempts; i += 1) {
    const token = await getToken()
    if (token) {
      return token
    }
    await new Promise((resolve) => {
      window.setTimeout(resolve, 150 * (i + 1))
    })
  }
  return null
}

export function MeProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useAuth()
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadMe = useCallback(async () => {
    if (!isSignedIn) {
      setMe(null)
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const token = await waitForToken(getToken)
      if (!token) {
        setMe(null)
        setError('Still signing you in — one moment.')
        return
      }
      setMe(await getMe(async () => token))
    } catch (err: unknown) {
      setMe(null)
      setError(
        err instanceof ApiError ? err.detail : "Couldn't load your profile.",
      )
    } finally {
      setLoading(false)
    }
  }, [isSignedIn, getToken])

  const refresh = useCallback(async () => {
    await loadMe()
  }, [loadMe])

  useEffect(() => {
    if (!isLoaded) {
      return
    }
    void loadMe()
  }, [isLoaded, loadMe])

  const canMutate = Boolean(
    isSignedIn && me?.emailVerified && me.status === 'active',
  )

  const messageFor = useCallback(
    (action: AuthAction): string | null => {
      if (!isSignedIn || loading || me === null) {
        return null
      }
      if (me.status !== 'active') {
        return 'Your account is suspended.'
      }
      if (!me.emailVerified) {
        return `Verify your email to ${ACTION_COPY[action]}.`
      }
      return null
    },
    [isSignedIn, loading, me],
  )

  const value = useMemo(
    () => ({ me, loading, error, refresh, canMutate, messageFor }),
    [me, loading, error, refresh, canMutate, messageFor],
  )

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>
}

export function useMe(): MeContextValue {
  const value = useContext(MeContext)
  if (value === null) {
    throw new Error('useMe must be used within MeProvider')
  }
  return value
}

export type { AuthAction }
